import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Descriptions, Input, List, Modal, Row, Select, Space, Table, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import api from '../../api/http';
import AuthedImage from '../../components/AuthedImage';

const { Text } = Typography;

const REPORT_STATUS_META = {
  PENDING_ANALYSIS: { color: 'gold', text: '待识别' },
  ANALYZED: { color: 'green', text: '已识别' },
  ANALYSIS_FAILED: { color: 'red', text: '识别失败' },
};

const TASK_STATUS_META = {
  pending: { color: 'gold', text: '待处理' },
  retry_wait: { color: 'orange', text: '待重试' },
  processing: { color: 'blue', text: '识别中' },
  success: { color: 'green', text: '成功' },
  failed: { color: 'red', text: '失败' },
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const dt = dayjs(value);
  return dt.isValid() ? dt.format('YYYY-MM-DD HH:mm:ss') : String(value);
};

const renderStatusTag = (status) => {
  const key = String(status || '').trim();
  const meta = REPORT_STATUS_META[key] || { color: 'default', text: key || '-' };
  return <Tag color={meta.color}>{meta.text}</Tag>;
};

const renderTaskStatusTag = (status) => {
  const key = String(status || '').trim();
  const meta = TASK_STATUS_META[key] || { color: 'default', text: key || '-' };
  return <Tag color={meta.color}>{meta.text}</Tag>;
};

function QuickShotPage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState('');

  const [detailVisible, setDetailVisible] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  const loadRecords = useCallback(async (nextPage = page, nextPageSize = pageSize) => {
    setLoading(true);
    try {
      const params = {
        page: nextPage,
        pageSize: nextPageSize,
      };
      if (keyword) params.keyword = keyword;
      if (status) params.status = status;

      const response = await api.get('/hazard-quick-shot/records', { params });
      const data = response?.data || {};
      setRecords(Array.isArray(data.records) ? data.records : []);
      setTotal(Number(data.total || 0));
      setPage(Number(data.page || nextPage));
      setPageSize(Number(data.pageSize || nextPageSize));
    } catch (error) {
      message.error(error?.response?.data?.error || error?.message || '加载随手拍列表失败');
    } finally {
      setLoading(false);
    }
  }, [keyword, page, pageSize, status]);

  useEffect(() => {
    loadRecords(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, status]);

  const onSearch = () => {
    setKeyword(keywordInput.trim());
    setPage(1);
  };

  const onReset = () => {
    setKeywordInput('');
    setKeyword('');
    setStatus('');
    setPage(1);
  };

  const openDetail = async (record) => {
    const reportId = Number(record?.id);
    if (!reportId) return;

    try {
      const response = await api.get(`/hazard-quick-shot/records/${reportId}`);
      setDetailRecord(response?.data?.record || null);
      setDetailVisible(true);
    } catch (error) {
      message.error(error?.response?.data?.error || error?.message || '加载详情失败');
    }
  };

  const pointCount = useMemo(() => {
    const points = Array.isArray(detailRecord?.analysis_points) ? detailRecord.analysis_points : [];
    return points.length;
  }, [detailRecord]);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 90,
      fixed: 'left',
    },
    {
      title: '设备名称',
      dataIndex: 'device_name',
      width: 180,
      render: (value) => value || '-',
    },
    {
      title: '地点',
      dataIndex: 'location',
      width: 180,
      render: (value) => value || '-',
    },
    {
      title: '图片',
      dataIndex: 'first_image',
      width: 120,
      render: (_, record) => {
        const image = record?.first_image;
        const url = image?.proxy_url || image?.file_url;
        if (!url) return <Text type="secondary">无</Text>;
        return (
          <AuthedImage
            src={url}
            alt="随手拍"
            style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid #f0f0f0' }}
          />
        );
      },
    },
    {
      title: '隐患描述',
      dataIndex: 'hazard_description',
      width: 360,
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      title: '提交人',
      dataIndex: 'submitted_by_name',
      width: 130,
      render: (value) => value || '-',
    },
    {
      title: '识别状态',
      dataIndex: 'report_status',
      width: 120,
      render: (value) => renderStatusTag(value),
    },
    {
      title: '任务状态',
      dataIndex: 'task_status',
      width: 120,
      render: (value) => renderTaskStatusTag(value),
    },
    {
      title: '提交时间',
      dataIndex: 'created_at',
      width: 180,
      render: (value) => formatDateTime(value),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Button size="small" onClick={() => openDetail(record)}>
          详情
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 12 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input
              placeholder="设备/地点/描述/提交人"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onPressEnter={onSearch}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="识别状态"
              value={status || undefined}
              onChange={(value) => setStatus(value || '')}
              allowClear
              options={Object.keys(REPORT_STATUS_META).map((key) => ({ value: key, label: REPORT_STATUS_META[key].text }))}
            />
          </Col>
          <Col xs={24} sm={24} md={10} lg={8}>
            <Space>
              <Button type="primary" onClick={onSearch}>查询</Button>
              <Button onClick={onReset}>重置</Button>
              <Button onClick={() => loadRecords(page, pageSize)}>刷新</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card title="隐患随手拍列表">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={records}
          loading={loading}
          scroll={{ x: 1600 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (count) => `共 ${count} 条`,
            onChange: (nextPage, nextPageSize) => {
              loadRecords(nextPage, nextPageSize);
            },
          }}
        />
      </Card>

      <Modal
        title={`随手拍详情 #${detailRecord?.id || ''}`}
        open={detailVisible}
        width={980}
        footer={null}
        onCancel={() => {
          setDetailVisible(false);
          setDetailRecord(null);
        }}
      >
        {detailRecord ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="设备名称">{detailRecord.device_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="地点">{detailRecord.location || '-'}</Descriptions.Item>
              <Descriptions.Item label="提交人">{detailRecord.submitted_by_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="识别状态">{renderStatusTag(detailRecord.report_status)}</Descriptions.Item>
              <Descriptions.Item label="任务状态">{renderTaskStatusTag(detailRecord.task_status)}</Descriptions.Item>
              <Descriptions.Item label="提交时间">{formatDateTime(detailRecord.created_at)}</Descriptions.Item>
              <Descriptions.Item label="识别完成时间">{formatDateTime(detailRecord.analysis_completed_at)}</Descriptions.Item>
              <Descriptions.Item label="排查点数量">{pointCount}</Descriptions.Item>
              <Descriptions.Item label="隐患描述" span={2}>{detailRecord.hazard_description || '-'}</Descriptions.Item>
              <Descriptions.Item label="识别摘要" span={2}>{detailRecord.analysis_summary || '-'}</Descriptions.Item>
              {detailRecord.last_error ? (
                <Descriptions.Item label="任务错误" span={2}>
                  <Text type="danger">{detailRecord.last_error}</Text>
                </Descriptions.Item>
              ) : null}
            </Descriptions>

            <Card size="small" title="现场图片">
              <Space wrap>
                {(detailRecord.images || []).length ? (
                  detailRecord.images.map((image) => {
                    const src = image?.proxy_url || image?.file_url;
                    if (!src) return null;
                    return (
                      <AuthedImage
                        key={image.id}
                        src={src}
                        alt={image.file_name || '随手拍图片'}
                        style={{ width: 160, height: 120, objectFit: 'cover', borderRadius: 6, border: '1px solid #f0f0f0' }}
                      />
                    );
                  })
                ) : (
                  <Text type="secondary">暂无图片</Text>
                )}
              </Space>
            </Card>

            <Card size="small" title="AI隐患排查点">
              <List
                locale={{ emptyText: '暂无识别结果' }}
                dataSource={Array.isArray(detailRecord.analysis_points) ? detailRecord.analysis_points : []}
                renderItem={(item, index) => (
                  <List.Item>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Text strong>{`${index + 1}. ${item?.point || '-'}`}</Text>
                      <Text>风险等级：{item?.riskLevel || '-'}</Text>
                      <Text>判断依据：{item?.evidence || '-'}</Text>
                      <Text>整改建议：{item?.suggestion || '-'}</Text>
                    </Space>
                  </List.Item>
                )}
              />
            </Card>
          </Space>
        ) : null}
      </Modal>
    </div>
  );
}

export default QuickShotPage;
